import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';

function StoredBusinessesPage() {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mailLoading, setMailLoading] = useState(false);
    const [mailStatus, setMailStatus] = useState({ type: '', message: '' });

    // State for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10); // You can change this value

    const [tab, setTab] = useState(() => {
        const storedTab = sessionStorage.getItem('businessesTab');
        return storedTab !== null ? Number(storedTab) : 0;
    });

    useEffect(() => {
        sessionStorage.setItem('businessesTab', tab.toString());
        // Reset to first page whenever the tab changes
        setCurrentPage(1); 
    }, [tab]);

    useEffect(() => {
        const fetchBusinesses = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/businesses`);
                const data = response.data;
                setBusinesses(data.map((business) => ({ ...business, id: business._id })));
            } catch (err) {
                setError('Failed to fetch businesses. Please try again later.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchBusinesses();
    }, []);

    const today = moment().startOf('day');
    const todaysBusinesses = businesses.filter(b =>
        moment(b.scrapedAt).isSameOrAfter(today)
    );

    const handleSendMail = async () => {
        setMailLoading(true);
        setMailStatus({ type: '', message: '' });
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/businesses/send-report-mail`);
            setMailStatus({ type: 'success', message: response.data.msg || 'Mail sent successfully!' });
        } catch (err) {
            setMailStatus({ type: 'error', message: err.response?.data?.msg || 'Failed to send mail.' });
        } finally {
            setMailLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this business?')) return;
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/businesses/${id}`);
            setBusinesses((prev) => prev.filter((b) => b.id !== id));
        } catch (err) {
            alert('Failed to delete business.');
        }
    };

    const columns = [
        { key: 'name', label: 'Business Name', className: 'font-semibold' },
        { key: 'rating', label: 'Rating' },
        { key: 'reviews', label: 'Reviews' },
        { key: 'phone', label: 'Phone' },
        { key: 'address', label: 'Address' },
        { key: 'area', label: 'Area' },
        { key: 'experience', label: 'Experience' },
        { key: 'availability', label: 'Availability' },
        { key: 'justDialUrl', label: 'JustDial URL', render: (value) => <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">{value}</a> },
        { key: 'scrapedAt', label: 'Scraped At', render: (value) => moment(value).format('YYYY-MM-DD HH:mm') },
        { key: 'searchQueryUsed', label: 'Search Query' },
        {
            key: 'actions', label: 'Actions', render: (_, row) => (
                <button
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded shadow"
                    onClick={() => handleDelete(row.id)}
                    title="Delete"
                >
                    &#128465;
                </button>
            )
        }
    ];

    const rows = tab === 0 ? businesses : todaysBusinesses;

    // Logic for displaying current businesses
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentBusinesses = rows.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(rows.length / itemsPerPage);

    // Pagination handlers
    const goToPrevPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900 py-8 px-4 flex flex-col items-center">
            <div className="bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full p-8">
                <h2 className="text-3xl font-extrabold text-center text-indigo-300 mb-2 drop-shadow-lg">
                    All Stored Businesses
                </h2>

                <div className="flex gap-2 justify-center mb-6">
                    <button
                        className={`px-6 py-2 rounded-t-lg font-bold transition ${
                            tab === 0 ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-indigo-300'
                        }`}
                        onClick={() => setTab(0)}
                    >
                        All
                    </button>
                    <button
                        className={`px-6 py-2 rounded-t-lg font-bold transition ${
                            tab === 1 ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-indigo-300'
                        }`}
                        onClick={() => setTab(1)}
                    >
                        Today's Scrape
                    </button>
                </div>

                <div className="flex justify-between items-center mb-4">
                    <button
                        className={`px-6 py-2 rounded-lg font-bold text-white transition shadow-lg ${
                            mailLoading ? 'bg-gray-500 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
                        }`}
                        onClick={handleSendMail}
                        disabled={mailLoading}
                    >
                        {mailLoading ? 'Sending Mail...' : 'Send Report Mail'}
                    </button>
                    {rows.length > 0 && (
                        <span className="text-gray-300 text-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                    )}
                </div>

                {mailStatus.message && (
                    <div className={`mb-4 px-4 py-3 rounded text-center text-base font-semibold ${
                        mailStatus.type === 'success'
                            ? 'bg-green-900 text-green-300'
                            : mailStatus.type === 'error'
                            ? 'bg-red-900 text-red-300'
                            : 'bg-indigo-900 text-indigo-300'
                    }`}>
                        {mailStatus.message}
                    </div>
                )}

                {loading && (
                    <div className="w-full flex justify-center items-center mb-4">
                        <div className="animate-pulse h-2 w-1/2 bg-indigo-500 rounded"></div>
                    </div>
                )}

                {error && (
                    <div className="mb-4 px-4 py-3 rounded bg-red-900 text-red-300 text-center text-base font-semibold">
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <div className="overflow-x-auto bg-gray-900 rounded-lg shadow-lg">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead>
                                    <tr>
                                        {columns.map(col => (
                                            <th key={col.key} className="px-4 py-2 text-left text-indigo-200 bg-gray-800 font-bold">{col.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentBusinesses.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-700 transition">
                                            {columns.map(col => (
                                                <td key={col.key} className={`px-4 py-2 text-gray-100 ${col.className || ''}`}>
                                                    {col.render
                                                        ? col.render(row[col.key], row)
                                                        : row[col.key]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {rows.length > itemsPerPage && (
                            <div className="flex justify-center mt-6 space-x-4">
                                <button
                                    onClick={goToPrevPage}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                                        currentPage === 1
                                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    }`}
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                                        currentPage === totalPages
                                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    }`}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}

                {!loading && !error && rows.length === 0 && (
                    <div className="mt-4 px-4 py-3 rounded bg-indigo-900 text-indigo-300 text-center text-base font-semibold">
                        {tab === 0
                            ? 'No businesses found in the database. Try searching and scraping some!'
                            : "No businesses scraped today."}
                    </div>
                )}
            </div>
        </div>
    );
}

export default StoredBusinessesPage;